import { computed, type ComputedRef } from 'vue'
import { useOrderStore } from '@/stores/order'
import { useAppFlow } from '@/composables/core/useAppFlow'
import { useNotifications } from '@/composables/core/useNotifications'
import { useReservationSessionGuard } from '@/composables/features/useReservationSessionGuard'
import { parseApiError } from '@/utils/errorHandler'
import type { OrderItem } from '@/interfaces'

/**
 * Order composable
 * Handles cart and order management.
 * Cart is persisted to localStorage only in smart-dine-in flow.
 */
export function useOrder() {
  const orderStore = useOrderStore()
  const { currentFlow } = useAppFlow()
  const { showSuccess, showError } = useNotifications()
  const { validateReservationStillActive } = useReservationSessionGuard()

  const cart = computed(() => orderStore.cart)
  const cartItemCount: ComputedRef<number> = computed(() => orderStore.cartItemCount)
  const cartTotal: ComputedRef<number> = computed(() => orderStore.cartTotal)
  const sosDisable: ComputedRef<boolean> = computed(() => orderStore.sosDisable)

  const addToCart = (item: OrderItem) => {
    orderStore.addToCart(item)
    if (currentFlow.value === 'smart-dine-in') {
      orderStore.saveCartToLocalStorage()
    }
  }

  const removeFromCart = (itemId: string, attributeId: string) => {
    orderStore.removeFromCart(itemId, attributeId)
    if (currentFlow.value === 'smart-dine-in') {
      orderStore.saveCartToLocalStorage()
    }
    showSuccess('Item removed from cart')
  }

  const updateCartItem = (
    previousItemId: string,
    previousAttributeId: string,
    newItem: OrderItem
  ) => {
    orderStore.updateCartItem(previousItemId, previousAttributeId, newItem)
    if (currentFlow.value === 'smart-dine-in') {
      orderStore.saveCartToLocalStorage()
    }
    showSuccess('Cart updated')
  }

  const clearCart = (options?: { silent?: boolean }) => {
    orderStore.clearCart()
    if (!options?.silent) {
      showSuccess('Cart cleared')
    }
  }

  /** Fire-and-forget: refresh the active order item count badge after a successful confirmation. */
  const refreshOrderBadge = (reservationId: string | number | undefined): void => {
    if (!reservationId) return
    void orderStore
      .getMyOrderCart({ active_reservation_id: String(reservationId) })
      .then(res => {
        const payload = ((res as Record<string, unknown>).data ?? res) as Record<string, unknown>
        const items = (payload?.order_items as unknown[]) ?? []
        orderStore.setActiveOrderItemCount(items.length)
      })
      .catch(() => {
        // Badge update is non-critical; ignore errors silently
      })
  }

  const confirmOrderItem = async (
    item: OrderItem,
    skipBadgeRefresh = false,
    options?: { silentSuccess?: boolean }
  ) => {
    // Always call check_active_reservation before each item confirmation when we have stored reservation
    const reservationOk = await verifyActiveReservationBeforeOrder()
    if (!reservationOk) return false

    try {
      const response = await orderStore.confirmOrderItem(item)
      if (!response.error) {
        if (!options?.silentSuccess) {
          showSuccess('Order item confirmed')
        }
        if (!skipBadgeRefresh) refreshOrderBadge(item.active_reservation_id)
        return true
      } else {
        showError(response.error as string)
        return false
      }
    } catch (err: any) {
      showError(err.message || 'Failed to confirm order')
      return false
    }
  }

  const getMyOrderCart = async (params: { active_reservation_id: string }) => {
    try {
      return await orderStore.getMyOrderCart(params)
    } catch (err: any) {
      showError('Failed to load cart')
      throw err
    }
  }

  /**
   * Calls check_active_reservation_new before placing order (same payload as host check; allows tablemates with PIN).
   * Required when we have stored active_reservation_info. Returns true if order can proceed, false otherwise.
   */
  const verifyActiveReservationBeforeOrder = async (): Promise<boolean> => {
    const raw = localStorage.getItem('active_reservation_info')
    if (!raw) return true
    try {
      const info = JSON.parse(raw) as { reservationId?: string }
      const reservationId = info?.reservationId
      if (!reservationId) {
        showError('Cannot verify reservation; order not allowed')
        return false
      }
      const activeReservationId = Number(reservationId)
      if (Number.isNaN(activeReservationId)) {
        showError('Order is closed, cannot order')
        return false
      }
      const validation = await validateReservationStillActive(activeReservationId)
      if (!validation.ok) {
        showError(validation.message || 'Order is closed, cannot order')
        return false
      }
      return true
    } catch {
      showError('Order is closed, cannot order')
      return false
    }
  }

  const placeOrder = async (reservationId: string): Promise<boolean> => {
    if (!reservationId || cart.value.items.length === 0) {
      showError('Cart is empty or reservation not found')
      return false
    }

    try {
      for (const item of cart.value.items) {
        const orderItem: OrderItem = {
          ...item,
          active_reservation_id: reservationId,
        }
        // Skip per-item badge refresh; do a single refresh after all items are confirmed
        const success = await confirmOrderItem(orderItem, true, { silentSuccess: true })
        if (!success) {
          return false
        }
      }

      clearCart({ silent: true })
      showSuccess('Order placed successfully')
      // Single badge refresh after the full cart is placed
      refreshOrderBadge(reservationId)
      return true
    } catch (error: unknown) {
      showError(parseApiError(error).message)
      return false
    }
  }

  return {
    // State
    cart,
    cartItemCount,
    cartTotal,
    sosDisable,

    // Actions
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    confirmOrderItem,
    getMyOrderCart,
    placeOrder,
  }
}
